import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    Table,
    Form,
    Button,
    Spinner,
    Badge,
    Alert,
    InputGroup
} from "react-bootstrap";
import {
    FaMoneyBillWave,
    FaCalculator,
    FaChartLine,
    FaCog,
    FaDownload
} from "react-icons/fa";
import {
    getFirestore,
    collection,
    query,
    where,
    getDocs,
    collectionGroup
} from "firebase/firestore";
import * as XLSX from "xlsx";
import firebaseApp from "../../firebase/credenciales";

const db = getFirestore(firebaseApp);

function ImpactoFinanciero({ usuario }) {
    const [loading, setLoading] = useState(false);
    const [periodo, setPeriodo] = useState(new Date().getFullYear());

    // Configuración de cálculo
    const [config, setConfig] = useState({
        factorIndirecto: 1.5, // Costos ocultos (reemplazo, productividad, etc)
        sueldoPromedioDefault: 1500, // Si no hay sueldo real
        diasLaborablesMes: 30
    });

    const [datos, setDatos] = useState([]);
    const [resumen, setResumen] = useState({
        costoDirecto: 0,
        costoIndirecto: 0,
        costoTotal: 0,
        diasTotal: 0
    });

    const [mostrarConfig, setMostrarConfig] = useState(false);

    useEffect(() => {
        calcularImpacto();
    }, [usuario, periodo, config]); // Recalcular si cambia la config

    const calcularImpacto = async () => {
        setLoading(true);
        try {
            const ruc = usuario.ruc || usuario.rucEmpresa;

            // 1. Obtener empleados con sus sueldos
            const usersRef = collection(db, "users");
            const qUsers = query(
                usersRef,
                where("rucEmpresa", "==", ruc),
                where("activo", "==", true)
            );
            const usersSnap = await getDocs(qUsers);
            const empleadosMap = {}; // dni -> { nombre, sueldo, area }

            usersSnap.forEach(doc => {
                const d = doc.data();
                empleadosMap[d.dni] = {
                    nombre: d.nombreCompleto || d.nombre,
                    sueldo: Number(d.sueldo) || config.sueldoPromedioDefault,
                    area: d.area || "General"
                };
            });

            // 2. Obtener ausencias
            const inicioAno = new Date(periodo, 0, 1);
            const finAno = new Date(periodo, 11, 31);

            const ausenciasRef = collectionGroup(db, "solicitudes_ausencia");
            const qAusencias = query(
                ausenciasRef,
                where("rucEmpresa", "==", ruc),
                where("fechaInicio", ">=", inicioAno),
                where("fechaInicio", "<=", finAno),
                where("estado", "==", "aprobado")
            );

            const snapAusencias = await getDocs(qAusencias);

            // 3. Procesar y consolidar por empleado
            const impactoPorEmpleado = {}; // dni -> { dias, costoDirecto }

            snapAusencias.forEach(doc => {
                const data = doc.data();
                const dni = data.empleadoDni;
                const dias = data.totalDias || 0;

                // Si no tenemos al empleado en el mapa (quizas inactivo), usamos default
                const empleadoInfo = empleadosMap[dni] || {
                    nombre: data.nombreEmpleado || `DNI ${dni}`,
                    sueldo: config.sueldoPromedioDefault,
                    area: "Desconocido"
                };

                const costoDia = empleadoInfo.sueldo / config.diasLaborablesMes;
                const costoDirectoAusencia = costoDia * dias;

                if (!impactoPorEmpleado[dni]) {
                    impactoPorEmpleado[dni] = {
                        ...empleadoInfo,
                        dni,
                        dias: 0,
                        costoDirecto: 0,
                        count: 0
                    };
                }

                impactoPorEmpleado[dni].dias += dias;
                impactoPorEmpleado[dni].costoDirecto += costoDirectoAusencia;
                impactoPorEmpleado[dni].count += 1;
            });

            // 4. Calcular totales y lista final
            const listaFinal = Object.values(impactoPorEmpleado).map(item => {
                const costoIndirecto = item.costoDirecto * (config.factorIndirecto - 1);
                const costoTotal = item.costoDirecto * config.factorIndirecto;
                return {
                    ...item,
                    costoIndirecto,
                    costoTotal
                };
            });

            listaFinal.sort((a, b) => b.costoTotal - a.costoTotal);

            // Sumarizadores globales
            const totalDirecto = listaFinal.reduce((sum, item) => sum + item.costoDirecto, 0);
            const totalIndirecto = listaFinal.reduce((sum, item) => sum + item.costoIndirecto, 0);
            const totalTotal = totalDirecto + totalIndirecto;
            const totalDias = listaFinal.reduce((sum, item) => sum + item.dias, 0);

            setDatos(listaFinal);
            setResumen({
                costoDirecto: totalDirecto,
                costoIndirecto: totalIndirecto,
                costoTotal: totalTotal,
                diasTotal: totalDias
            });

        } catch (error) {
            console.error("Error calculando impacto:", error);
        } finally {
            setLoading(false);
        }
    };

    const exportarExcel = () => {
        const wsData = datos.map(item => ({
            Empleado: item.nombre,
            DNI: item.dni,
            Área: item.area,
            "Sueldo Base": item.sueldo,
            "Días Ausente": item.dias,
            "Costo Directo": item.costoDirecto,
            "Costo Indirecto (Est.)": item.costoIndirecto,
            "Costo Total Impacto": item.costoTotal
        }));

        const ws = XLSX.utils.json_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Impacto Financiero");
        XLSX.writeFile(wb, `Impacto_Financiero_${periodo}.xlsx`);
    };

    return (
        <div
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0"
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                <Card
                    className="shadow-lg border-0"
                    style={{ borderRadius: "15px", overflow: "hidden" }}
                >
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(45deg, #2c3e50, #3498db)",
                            borderBottom: "3px solid #f39c12"
                        }}
                    >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
                        <div className="d-flex justify-content-between align-items-center px-4">
                            <div>
                                <h2 className="fw-bold mb-1">
                                    <FaMoneyBillWave className="me-3" />
                                    Impacto Financiero
                                </h2>
                                <p className="mb-0 opacity-75">
                                    Análisis de costos directos e indirectos del ausentismo
                                </p>
                            </div>
                            <Button
                                variant="light"
                                size="sm"
                                onClick={() => setMostrarConfig(!mostrarConfig)}
                            >
                                <FaCog className="me-2" /> Configurar Cálculo
                            </Button>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>

                        {/* Panel de Configuración */}
                        {mostrarConfig && (
                            <Card className="shadow-sm border-0 mb-4 bg-light">
                                <Card.Body>
                                    <h6 className="fw-bold mb-3">Parámetros de Estimación</h6>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Factor de Costo Indirecto</Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.1"
                                                        value={config.factorIndirecto}
                                                        onChange={(e) => setConfig({ ...config, factorIndirecto: Number(e.target.value) })}
                                                    />
                                                    <InputGroup.Text>x</InputGroup.Text>
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Multiplicador sobre costo directo (Ej: 1.5 = +50% extra)
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label>Sueldo Promedio Default</Form.Label>
                                                <InputGroup>
                                                    <InputGroup.Text>S/</InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        value={config.sueldoPromedioDefault}
                                                        onChange={(e) => setConfig({ ...config, sueldoPromedioDefault: Number(e.target.value) })}
                                                    />
                                                </InputGroup>
                                                <Form.Text className="text-muted">
                                                    Usado si el empleado no tiene sueldo registrado
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4} className="d-flex align-items-center">
                                            <Button variant="primary" onClick={calcularImpacto} disabled={loading}>
                                                <FaCalculator className="me-2" /> Recalcular
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Resumen Totales */}
                        <Row className="mb-4 text-center">
                            <Col md={4}>
                                <Card className="shadow-sm border-0 bg-white">
                                    <Card.Body>
                                        <h6 className="text-muted text-uppercase">Costo Directo Anual</h6>
                                        <h3 className="fw-bold text-primary">S/ {resumen.costoDirecto.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</h3>
                                        <small className="text-muted">Salarios pagados sin trabajar</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="shadow-sm border-0 bg-white">
                                    <Card.Body>
                                        <h6 className="text-muted text-uppercase">Costo Indirecto (Est.)</h6>
                                        <h3 className="fw-bold text-warning">S/ {resumen.costoIndirecto.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</h3>
                                        <small className="text-muted">Reemplazos, pérdida productividad, gestión</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="shadow bg-primary text-white border-0">
                                    <Card.Body>
                                        <h6 className="text-white-50 text-uppercase">Impacto Total</h6>
                                        <h2 className="fw-bold">S/ {resumen.costoTotal.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</h2>
                                        <small className="text-white-50">Suma total estimada</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Gráfico y Proyección */}
                        <Row className="mb-4">
                            <Col md={12}>
                                <Alert variant="info" className="d-flex align-items-center border-0 shadow-sm">
                                    <FaChartLine className="me-3 fs-3" />
                                    <div>
                                        <h6 className="fw-bold mb-1">Análisis de Retorno de Inversión (ROI)</h6>
                                        <p className="mb-0">
                                            Reducir el ausentismo en un <strong>20%</strong> podría generar un ahorro estimado de
                                            <strong> S/ {(resumen.costoTotal * 0.2).toLocaleString('es-PE', { minimumFractionDigits: 2 })}</strong> este año.
                                        </p>
                                    </div>
                                </Alert>
                            </Col>
                        </Row>

                        {/* Detalle por Empleado */}
                        <Card className="shadow-sm border-0">
                            <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                                <h6 className="fw-bold mb-0">Desglose por Empleado</h6>
                                <Button variant="outline-success" size="sm" onClick={exportarExcel}>
                                    <FaDownload className="me-2" /> Excel
                                </Button>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <Table responsive hover className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Empleado</th>
                                            <th className="text-center">Días Ausente</th>
                                            <th className="text-end">Costo Directo</th>
                                            <th className="text-end">Costo Indirecto</th>
                                            <th className="text-end">Impacto Total</th>
                                            <th className="text-center">% del Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={6} className="text-center py-5">
                                                    <Spinner animation="border" variant="primary" />
                                                </td>
                                            </tr>
                                        ) : datos.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="text-center py-4">No hay datos financieros para mostrar en este periodo.</td>
                                            </tr>
                                        ) : (
                                            datos.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <div className="fw-bold">{item.nombre}</div>
                                                        <small className="text-muted">{item.area}</small>
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge bg="secondary">{item.dias} días</Badge>
                                                    </td>
                                                    <td className="text-end">S/ {item.costoDirecto.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</td>
                                                    <td className="text-end text-muted">S/ {item.costoIndirecto.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</td>
                                                    <td className="text-end fw-bold text-danger">S/ {item.costoTotal.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</td>
                                                    <td className="text-center">
                                                        {((item.costoTotal / resumen.costoTotal) * 100).toFixed(1)}%
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>

                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
}

export default ImpactoFinanciero;
